import React, { useState } from 'react';
import { Card, Form, Button, Alert, Row, Col } from 'react-bootstrap';
import { CreditCard, CheckCircle, AlertCircle } from 'react-feather';
import http from '../../services/httpServices';
import DashboardLayout from "../common/DashboardLayout";

const UpdatePaymentInfoPage = () => {
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const userRole = Number(localStorage.getItem("userrole") || 0);

  const [formData, setFormData] = useState({
    cardNumber: '',
    expirationDate: '', // MMYY or YYYY-MM
    cardCode: '',
    firstName: '',
    lastName: '',
    zip: ''
  });
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null); // 'success', 'error'
  const [message, setMessage] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus(null);
    setMessage('');

    try {
      // Using standard routing (httpService adds /api base URL)
      // Use POST to bypass potential 405 Method Not Allowed on rigid networks
      const response = await http.post('payments/update-payment-info', {
        paymentDetails: formData
      });

      if (response.success) {
        setStatus('success');
        setMessage('Your payment method has been successfully updated.');
        setFormData({
          cardNumber: '',
          expirationDate: '',
          cardCode: '',
          firstName: '',
          lastName: '',
          zip: ''
        });
      }
    } catch (err) {
      setStatus('error');
      console.error('Update payment info error:', err);
      // Aggressive debugging
      const errorBody = err.response?.data || err.message;
      alert('PAYMENT ERROR: ' + JSON.stringify(errorBody, null, 2));

      setMessage(err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to update payment information. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout
      role={userRole}
      userType={user?.type}
      activeMenu="Payment"
      layoutType="topnav"
    >
      <div className="container-fluid p-4">
        <h2 className="mb-2">Update Payment Method</h2>
        <p className="text-muted mb-4">
          To cancel your subscription, email{' '}
          <a href="mailto:info@angelwatch.org">info@angelwatch.org</a> or call{' '}
          <a href="tel:+18003816794">1-800-381-6794</a>.
        </p>

        <Row>
          <Col md="6" lg="5">
            <Card>
              <Card.Body>
                <div className="d-flex align-items-center mb-4">
                  <CreditCard className="mr-2 text-primary" size={24} />
                  <h5 className="m-0">New Card Details</h5>
                </div>

                {status === 'success' && (
                  <Alert variant="success" className="d-flex align-items-center">
                    <CheckCircle size={18} className="mr-2" />
                    {message}
                  </Alert>
                )}

                {status === 'error' && (
                  <Alert variant="danger" className="d-flex align-items-center">
                    <AlertCircle size={18} className="mr-2" />
                    {message}
                  </Alert>
                )}

                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-3">
                    <Form.Label htmlFor="cardNumber">Card Number</Form.Label>
                    <Form.Control
                      type="text"
                      name="cardNumber"
                      id="cardNumber"
                      placeholder="0000 0000 0000 0000"
                      value={formData.cardNumber}
                      onChange={handleChange}
                      required
                      maxLength={19}
                    />
                  </Form.Group>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label htmlFor="expirationDate">Expiration (MMYY)</Form.Label>
                        <Form.Control
                          type="text"
                          name="expirationDate"
                          id="expirationDate"
                          placeholder="MMYY"
                          value={formData.expirationDate}
                          onChange={handleChange}
                          required
                          maxLength={5}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label htmlFor="cardCode">CVV</Form.Label>
                        <Form.Control
                          type="text"
                          name="cardCode"
                          id="cardCode"
                          placeholder="123"
                          value={formData.cardCode}
                          onChange={handleChange}
                          maxLength={4}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <hr className="my-3" />
                  <h6 className="mb-3">Billing Address (Optional)</h6>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label htmlFor="firstName">First Name</Form.Label>
                        <Form.Control
                          type="text"
                          name="firstName"
                          id="firstName"
                          value={formData.firstName}
                          onChange={handleChange}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label htmlFor="lastName">Last Name</Form.Label>
                        <Form.Control
                          type="text"
                          name="lastName"
                          id="lastName"
                          value={formData.lastName}
                          onChange={handleChange}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Label htmlFor="zip">Zip/Postal Code</Form.Label>
                    <Form.Control
                      type="text"
                      name="zip"
                      id="zip"
                      value={formData.zip}
                      onChange={handleChange}
                    />
                  </Form.Group>

                  <Button variant="primary" type="submit" className="w-100 mt-3" disabled={loading}>
                    {loading ? 'Updating...' : 'Update Payment Method'}
                  </Button>
                </Form>
              </Card.Body>
            </Card>
          </Col>

          <Col md="6" lg="7">
            <Card className="bg-light border-0">
              <Card.Body>
                <h5>Why update your payment method?</h5>
                <ul className="pl-3 mt-3">
                  <li className="mb-2">Ensure uninterrupted access to AngelWatch services.</li>
                  <li className="mb-2">Update expired or cancelled cards.</li>
                  <li className="mb-2">Switch to a preferred credit card for future billing.</li>
                </ul>
                <p className="text-muted small mt-4">
                  <strong>Secure Processing:</strong> Your payment information is securely transmitted directly to our payment processor (Authorize.Net) using industry-standard encryption. We do not store your full credit card number on our servers.
                </p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    </DashboardLayout>
  );
};

export default UpdatePaymentInfoPage;

